import {
  Avatar,
  ButtonBase,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Delete, Edit, InvoiceIcon } from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { format } from 'date-fns';
import { shortDateFormat } from 'Config';
import { IInvoice } from 'Context/CannabisContext';
interface InvoiceCardProps {
  index: number;
  invoice: IInvoice;
  editInvoice: () => void;
  deleteInvoice: () => void;
}
export const InvoiceCard = ({
  invoice,
  deleteInvoice,
  editInvoice,
  index,
}: InvoiceCardProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();

  return (
    <Grid
      key={index}
      container
      sx={{
        border: '1px solid',
        borderColor: 'primary.shade_100',
        p: isDesktop ? '24px' : '16px',
        bgcolor: 'primary.shade_50',
        borderRadius: '12px',
        alignItems: 'center',
      }}
      tabIndex={0}
    >
      {isDesktop ? (
        <Stack gap='16px' direction={'row'} alignItems='flex-start'>
          <Grid item md={0.5} alignSelf={'flex-start'}>
            <Stack
              sx={{
                width: '40px',
                p: '0',
                position: 'relative',
              }}
            >
              <Avatar
                src={InvoiceIcon}
                sx={{ height: '40px', width: '40px', mr: 2 }}
              />
            </Stack>
          </Grid>
          <Stack sx={{ width: 'fit-content' }}>
            <Typography
              variant='body6'
              sx={{ mr: isDesktop ? undefined : '6px' }}
            >
              {dictionary.number}
            </Typography>
            <Typography variant='body2'>{invoice.invoiceId}</Typography>
          </Stack>
          <Stack sx={{ width: 'fit-content' }}>
            <Typography
              variant='body6'
              sx={{ mr: isDesktop ? undefined : '6px' }}
            >
              {dictionary.date}
            </Typography>
            <Typography variant='body2'>
              {invoice.invoiceDate &&
                format(new Date(invoice.invoiceDate), shortDateFormat)}
            </Typography>
          </Stack>
          <Stack sx={{ width: 'fit-content' }}>
            <Typography
              variant='body6'
              sx={{ mr: isDesktop ? undefined : '6px' }}
            >
              {dictionary.amount}
            </Typography>
            <Typography variant='body1'>
              {invoice.invoiceTotal?.toLocaleString()} ₪
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <>
          <Stack flexDirection={'row'} gap='8px'>
            <Grid item alignSelf={'flex-start'}>
              <Stack
                sx={{
                  width: '40px',
                  p: '0',
                  position: 'relative',
                }}
              >
                <Avatar
                  src={InvoiceIcon}
                  sx={{ height: '40px', width: '40px', mr: 2 }}
                />
              </Stack>
            </Grid>
            <Stack>
              <Grid item>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    m: '4px 0',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant={'body2'} sx={{ mr: '6px' }}>
                    {dictionary.number}
                  </Typography>
                  <Typography variant='body2'>{invoice.invoiceId}</Typography>
                </Stack>
              </Grid>
              <Grid item>
                <Stack
                  sx={
                    isDesktop
                      ? { m: '0 32px' }
                      : {
                          flexDirection: 'row',
                          m: '4px 0',
                          alignItems: 'center',
                        }
                  }
                >
                  <Typography
                    variant={isDesktop ? 'body3' : 'body2'}
                    sx={{ mr: isDesktop ? undefined : '6px' }}
                  >
                    {dictionary.date}
                  </Typography>
                  <Typography variant='body2'>
                    {invoice.invoiceDate &&
                      format(new Date(invoice.invoiceDate), shortDateFormat)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item>
                <Stack
                  sx={
                    isDesktop
                      ? { m: '0 32px' }
                      : {
                          flexDirection: 'row',
                          m: '4px 0',
                          alignItems: 'center',
                        }
                  }
                >
                  <Typography
                    variant={isDesktop ? 'body3' : 'body2'}
                    sx={{ mr: isDesktop ? undefined : '6px' }}
                  >
                    {dictionary.amount}
                  </Typography>
                  <Typography variant='body1'>
                    {invoice.invoiceTotal?.toLocaleString()} ₪
                  </Typography>
                </Stack>
              </Grid>
            </Stack>
          </Stack>
        </>
      )}

      <Divider
        aria-hidden='true'
        sx={{ m: '18px 0', width: '100%', borderColor: 'primary.shade_200' }}
      />

      <Stack
        direction={'row'}
        justifyContent={'flex-end'}
        gap={'16px'}
        component='fieldset'
      >
        <ButtonBase
          onClick={() => editInvoice()}
          aria-label={`${dictionary.edit} ${invoice.invoiceId}`}
          sx={{ fontFamily: 'inherit' }}
        >
          <Stack direction={'row'} gap={'4px'}>
            <Avatar
              src={Edit}
              alt={dictionary.edit}
              sx={{
                height: '20px',
                width: '20px',
                cursor: 'pointer',
                border: 'none',
                bgcolor: 'transparent',
                p: 0,
              }}
            />
            <Typography fontFamily={'assistant'} variant={'body6'}>
              {dictionary.edit}
            </Typography>
          </Stack>
        </ButtonBase>
        <ButtonBase
          onClick={deleteInvoice}
          aria-label={`${dictionary.delete} ${invoice.invoiceId}`}
          sx={{ fontFamily: 'inherit' }}
        >
          <Stack direction={'row'} gap={'4px'}>
            <Avatar
              src={Delete}
              alt={dictionary.delete}
              sx={{
                height: '20px',
                width: '20px',
                cursor: 'pointer',
                border: 'none',
                bgcolor: 'transparent',
                p: 0,
              }}
            />
            <Typography fontFamily={'assistant'} variant={'body6'}>
              {dictionary.delete}
            </Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </Grid>
  );
};
